import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { API_SERVER } from '@pcplus/domain-core';
import { PermissionsService, EmptyGuid } from '@pcplus/security';
import { of } from 'rxjs';
import { filter, retry, switchMap } from 'rxjs/operators';
import { InvitationRequest } from '../dto/invitation-request';
import { InvitationStatus } from '../dto/invitation-status.enum';
import { MFAClusterSettings } from '../dto/mfa-cluster-settings';
import { MFASettings } from '../dto/mfa-settings';
import { PendingRegistrationsResponse } from '../dto/pending-registrations-response';
import { ProtectedResource } from '../dto/protected-resource';
import { ProtectedResourceInfo } from '../dto/protected-resource-info';
import { RegisteredUsersResponse } from '../dto/registered-users-response';
import { ResourceType } from '../dto/resource-type.enum';
import { UpdateProtectedResource } from '../dto/update-protected-resource';
import { UpdateRegisteredUserRequest } from '../dto/update-registered-user-request';
import { ValidateInvitations } from '../dto/validate-invitations';
import { RequestResult } from '@pcplus/domain-core';
import { SettingsInfo } from '../dto/settings-info';

@Injectable({
  providedIn: 'root'
})
export class MfaService {
  constructor(
    private http: HttpClient,
    private permissions: PermissionsService,
    @Inject(API_SERVER) private apiServer: string
  ) {}

  getTypeName(value: ResourceType) {
    switch (value) {
      case ResourceType.WindowsLogin:
        return $localize`Windows Login`;
      default:
        return '';
    }
  }

  getSupportCode(customerId: string, resourceId: string, userId: string) {
    return this.http
      .get<string>(
        this.apiServer +
          `api/SafeMFA/support/${customerId}/${resourceId}/${userId}`
      )
      .pipe(retry(3));
  }

  validateInvitations(
    customerId: string,
    resourceId: string,
    validate: ValidateInvitations
  ) {
    return this.http.post<ValidateInvitations>(
      this.apiServer +
        `api/SafeMFA/invitations-validate/${customerId}/${resourceId}`,
      validate
    );
  }

  issueInvitations(customerId: string, resourceId: string, ids: string[]) {
    return this.http.post<boolean>(
      this.apiServer + `api/SafeMFA/invitations/${customerId}/${resourceId}`,
      ids
    );
  }

  deleteUsers(customerId: string, resourceId: string, ids: string[]) {
    return this.http.post<boolean>(
      this.apiServer + `api/SafeMFA/users-delete/${customerId}/${resourceId}`,
      ids
    );
  }

  reissueUsers(customerId: string, ids: string[]) {
    return this.http.post<RequestResult>(
      this.apiServer + `api/SafeMFA/users-reissue/${customerId}`,
      ids
    );
  }

  updateUser(
    customerId: string,
    id: string,
    request: UpdateRegisteredUserRequest
  ) {
    return this.http.post<RequestResult>(
      this.apiServer + `api/SafeMFA/users/${customerId}/${id}`,
      request
    );
  }

  deletePendingRegistrations(customerId: string, ids: string[]) {
    return this.http.post<boolean>(
      this.apiServer + `api/SafeMFA/invitations-delete/${customerId}`,
      ids
    );
  }

  getInvitationStatusName(value: InvitationStatus) {
    switch (value) {
      case InvitationStatus.NotSent:
        return $localize`Not Sent`;
      case InvitationStatus.Queued:
        return $localize`Queued`;
      case InvitationStatus.SendFailure:
        return $localize`Send Failure`;
      case InvitationStatus.Sent:
        return $localize`Sent`;
      default:
        return '';
    }
  }

  getRegisteredUsers(customerId: string, resourceId: string) {
    return this.http
      .get<RegisteredUsersResponse>(
        this.apiServer + `api/SafeMFA/users/${customerId}/${resourceId}`
      )
      .pipe(retry(3));
  }

  getRegisteredUsersByEmail(customerId: string, email: string) {
    return this.http
      .get<RegisteredUsersResponse>(
        this.apiServer + `api/SafeMFA/users-by-email/${customerId}/${email}/`
      )
      .pipe(retry(3));
  }

  getPendingRegistrations(customerId: string, resourceId: string) {
    return this.http
      .get<PendingRegistrationsResponse>(
        this.apiServer + `api/SafeMFA/invitations/${customerId}/${resourceId}`
      )
      .pipe(retry(3));
  }

  getPendingRegistrationsByEmail(customerId: string, email: string) {
    return this.http
      .get<PendingRegistrationsResponse>(
        this.apiServer +
          `api/SafeMFA/invitations-by-email/${customerId}/${email}/`
      )
      .pipe(retry(3));
  }

  getProtectedResources(customerId: string) {
    return this.http
      .get<
        ProtectedResourceInfo[]
      >(this.apiServer + `api/SafeMFA/resources/${customerId}`)
      .pipe(retry(3));
  }

  getProtectedResource(customerId: string, resourceId: string) {
    return this.http
      .get<ProtectedResource>(
        this.apiServer + `api/SafeMFA/resources/${customerId}/${resourceId}`
      )
      .pipe(retry(3));
  }

  deleteProtectedResource(customerId: string, resourceId: string) {
    return this.http
      .delete<ProtectedResource>(
        this.apiServer + `api/SafeMFA/resources/${customerId}/${resourceId}`
      )
      .pipe(retry(3));
  }

  addResource(customerId: string, name: string, type: ResourceType) {
    return this.http.put(
      `${this.apiServer}api/SafeMFA/resources/${customerId}/${name}/${type}`,
      null
    );
  }

  addUsers(customerId: string, invitations: InvitationRequest) {
    return this.http.put<RequestResult>(
      `${this.apiServer}api/SafeMFA/invitations/${customerId}`,
      invitations
    );
  }

  simulateLogin(id: string) {
    return this.http.put<string | null>(
      `${this.apiServer}api/SafeMFA/auth/6c93512a-7730-482d-9c24-25e435b64353/ogiyK4JuXUm_7BoLp3NB_Q/${id}`,
      null
    );
  }

  isPushAuthorized(id: string) {
    return this.http.get<boolean>(
      `${this.apiServer}api/SafeMFA/auth/6c93512a-7730-482d-9c24-25e435b64353/ogiyK4JuXUm_7BoLp3NB_Q/${id}`
    );
  }

  saveClusterSettings(settings: SettingsInfo<MFAClusterSettings>) {
    return this.http.post<RequestResult>(
      this.apiServer + 'api/SafeMFA/clusterSettings',
      settings
    );
  }

  getClusterSettings() {
    return this.http
      .get<
        SettingsInfo<MFAClusterSettings>
      >(this.apiServer + 'api/SafeMFA/clusterSettings')
      .pipe(retry(3));
  }

  getSettings() {
    return this.permissions.selectedCustomer.pipe(
      filter((val) => val !== null && val !== EmptyGuid),
      switchMap((val) =>
        this.http
          .get<
            SettingsInfo<MFASettings>
          >(this.apiServer + `api/SafeMFA/settings/${val!}`)
          .pipe(retry(3))
      )
    );
  }

  updateProtectedResource(
    resourceId: string,
    customerId: string,
    update: UpdateProtectedResource
  ) {
    return this.http.post<ProtectedResource>(
      this.apiServer + `api/SafeMFA/resources/${customerId}/${resourceId}`,
      update
    );
  }

  downloadProvider(resourceId: string, customerId: string) {
    console.log('download provider');
    return this.http
      .get(this.apiServer + `api/Common/agent/${resourceId}/${customerId}`, {
        responseType: 'blob'
      })
      .pipe(
        switchMap((value: Blob) => {
          console.log('preparing provider');
          const fileName = 'SkyberTeamSafeMFA.exe';
          const objectUrl: string = URL.createObjectURL(value);
          const a: HTMLAnchorElement = document.createElement('a');
          a.href = objectUrl;
          a.download = fileName;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          URL.revokeObjectURL(objectUrl);
          return of(true);
        })
      );
  }

  saveSettings(settings: SettingsInfo<MFASettings>) {
    return this.http
      .post<RequestResult>(`${this.apiServer}api/SafeMFA/settings`, settings)
      .pipe(retry(3));
  }
}
